import { answersOfLength } from './answersAll';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type Language } from './languages';
import { isSupportedLength, type SupportedLength } from './lengths';
import { isValidGuess } from './words';

/** One length's word data, however it was obtained. */
export interface LengthData {
  answers: readonly string[];
  /** Accepted-guess membership for this length, case-insensitive. */
  isValid: (word: string) => boolean;
}

const cache = new Map<string, Promise<LengthData>>();

function pack(answers: readonly string[], guesses: readonly string[]): LengthData {
  const set = new Set(guesses);
  return { answers, isValid: (word) => set.has(word.toLowerCase()) };
}

async function load(length: SupportedLength, language: Language): Promise<LengthData> {
  // Answers are always already here (answersAll.ts is in the main bundle,
  // because the daily needs them offline — spec §10.2). Only the ~30k-word
  // accepted-guess list is worth a chunk.
  const answers = answersOfLength(length, language);
  if (language === 'nl') {
    // Every Dutch list is lazy, length 5 included: unlike English, no
    // Dutch guess list is in the main bundle for `isValidGuess` to reuse.
    switch (length) {
      case 4: { const m = await import('./data/nl/guesses4'); return pack(answers, m.GUESSES_NL_4); }
      case 5: { const m = await import('./data/nl/guesses5'); return pack(answers, m.GUESSES_NL_5); }
      case 6: { const m = await import('./data/nl/guesses6'); return pack(answers, m.GUESSES_NL_6); }
      case 7: { const m = await import('./data/nl/guesses7'); return pack(answers, m.GUESSES_NL_7); }
    }
  }
  switch (length) {
    case 5: return { answers, isValid: isValidGuess };
    case 4: { const m = await import('./data/data4-guesses'); return pack(answers, m.GUESSES_4); }
    case 6: { const m = await import('./data/data6-guesses'); return pack(answers, m.GUESSES_6); }
    case 7: { const m = await import('./data/data7-guesses'); return pack(answers, m.GUESSES_7); }
  }
}

/**
 * Word lists for one language's length, on demand. Every list except the
 * bundled English 5-letter one is a dynamic import, which Vite emits as
 * its own chunk (spec §10.5) — which is only true as long as nothing in
 * packages/web reaches `wordsAll.ts` and makes those modules statically
 * reachable.
 *
 * Rejects for an unplayable length; callers gate on `isSupportedLength`
 * first. A failed load is not cached, so a retry actually retries.
 */
export function loadLength(
  length: number,
  language: Language = DEFAULT_LANGUAGE,
): Promise<LengthData> {
  const key = `${language}:${length}`;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  if (!isSupportedLength(length)) {
    return Promise.reject(new Error(`unsupported word length: ${length}`));
  }
  const pending = load(length, language);
  cache.set(key, pending);
  void pending.catch(() => cache.delete(key));
  return pending;
}

/**
 * The language whose accepted-guess list contains `word`, with that
 * language's data — or null if no language we play accepts it.
 *
 * The lazy counterpart of `wordsAll.languageAcceptingGuess`, and the one
 * the web has to use: `wordsAll` statically imports every list, so a web
 * import of it would drag ~800 KB into the main bundle and un-split every
 * chunk. Here each candidate language is a dynamic import, and `preferred`
 * is tried first so the common case — a challenge in the language you are
 * already playing — costs exactly one chunk.
 *
 * Used for challenge words, which arrive as a bare word from a link: the
 * word itself is the only evidence of what language it belongs to when
 * the challenge predates the language field (spec §4.2).
 */
export async function loadLengthAccepting(
  word: string,
  preferred: Language = DEFAULT_LANGUAGE,
): Promise<{ language: Language; data: LengthData } | null> {
  const w = word.toLowerCase();
  const order = [preferred, ...SUPPORTED_LANGUAGES.filter((l) => l !== preferred)];
  for (const language of order) {
    try {
      const data = await loadLength(w.length, language);
      if (data.isValid(w)) return { language, data };
    } catch {
      // An unplayable length, or a chunk that would not load. Try the next
      // language: one language's failed chunk must not decide for the rest.
    }
  }
  return null;
}
